import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    Button, 
    Box, 
    CircularProgress, 
    Typography,
    Alert
} from '@mui/material';
import { useUser } from '../contexts/UserContext';

const PdfPreviewModal = ({ 
    open, 
    onClose, 
    selectedDetalles, 
    onConfirm, 
    actionType,  // 'prevalidar', 'validar', 'invalidar', 'imprimir'
    title = "Vista Previa del PDF",
    confirmText = "Continuar",
    cancelText = "Cancelar"
}) => {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    
    // Función para determinar el idTipoValidacion según la acción
    const getIdTipoValidacion = () => {
        switch(actionType) {
            case 'prevalidar':
                return 2; // Va a pasar a Pre Valido
            case 'validar':
                return 3; // Va a pasar a Validado
            case 'invalidar':
                return 2; // Regresa a Pre Valido
            case 'imprimir':
            default:
                // Para imprimir, usar el estado actual del primer detalle
                return selectedDetalles[0]?.idTipoAprobacion || 1;
        }
    };

    useEffect(() => {
        if (open && selectedDetalles && selectedDetalles.length > 0) {
            generatePdfPreview();
        }
        
        // Cleanup PDF URL when modal closes
        return () => {
            if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }
        };
    }, [open, selectedDetalles]);

    const generatePdfPreview = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Extract detail IDs from selected details
            const detailIds = selectedDetalles.map(detalle => detalle.idDetalle);
            
            // Obtener el usuario actual y formatearlo a 20 caracteres
            const usuarioActual = (user ? user.codigoUsuario : "ADMIN").padEnd(20, ' ');
            
            // Determinar el tipo de validación según la acción que se va a ejecutar
            const idTipoValidacion = getIdTipoValidacion();
            
            // Construir la URL con los parámetros
            const apiUrl = `${server}/MgAnalisisGeneralResultadosAnalitoes/ImprimirvariosSeleccionadoExamenFormato?usuario=${encodeURIComponent(usuarioActual)}&idTipoValidacion=${idTipoValidacion}`;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(detailIds)
            });

            if (!response.ok) {
                // Deserializar error específico para análisis con resultados no válidos
                let errorMessage = `Error al generar el PDF: ${response.status} ${response.statusText}`;
                
                try {
                    const errorData = await response.json();
                    
                    if (errorData.detail) {
                        // Error con formato RFC 7231 (como el de resultados no válidos)
                        errorMessage = errorData.detail;
                    } else if (errorData.message) {
                        // Error con formato message estándar
                        errorMessage = errorData.message;
                    } else if (errorData.errors) {
                        // Error con formato de validación
                        errorMessage = Object.values(errorData.errors).flat().join(', ');
                    }
                } catch (parseError) {
                    // Si no se puede parsear el JSON, mantener el mensaje por defecto
                    console.error('Error parsing response JSON:', parseError);
                }
                
                throw new Error(errorMessage);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (err) {
            console.error('Error generating PDF preview:', err);
            setError(err.message || 'Error al generar la vista previa del PDF');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        setError(null);
        onClose();
    };

    const handleConfirm = () => {
        onConfirm();
        handleClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            fullScreen
            PaperProps={{
                style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    maxHeight: '100vh',
                    margin: 0
                }
            }}
        >
            <DialogTitle>
                {title}
                <Typography variant="body2" color="textSecondary">
                    {selectedDetalles ? `${selectedDetalles.length} examen(es) seleccionado(s)` : ''}
                </Typography>
            </DialogTitle>
            
            <DialogContent style={{ 
                padding: 0, 
                display: 'flex', 
                flexDirection: 'column',
                flex: 1,
                overflow: 'hidden'
            }}>
                {loading && (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        flex={1}
                        minHeight="50vh"
                    >
                        <CircularProgress 
                            size={60} 
                            sx={{ 
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                },
                                animation: 'spin 0.5s linear infinite'
                            }}
                        />
                        <Typography variant="body1" style={{ marginTop: 16 }}>
                            Generando vista previa del PDF...
                        </Typography>
                    </Box>
                )}
                
                {error && (
                    <Box 
                        p={3}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flex={1}
                        minHeight="50vh"
                    >
                        <Alert severity="error" style={{ maxWidth: '600px' }}>
                            {error}
                        </Alert>
                    </Box>
                )}
                
                {pdfUrl && !loading && !error && (
                    <Box style={{ 
                        flex: 1, 
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <iframe
                            src={pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                flex: 1
                            }}
                            title="Vista previa del PDF"
                        />
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions style={{ padding: '16px 24px' }}>
                <Button 
                    onClick={handleClose} 
                    color="secondary"
                    variant="outlined"
                >
                    {cancelText}
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    color="primary"
                    variant="contained"
                    disabled={loading || error || !pdfUrl}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PdfPreviewModal;
